import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Video, 
  VideoOff, 
  RotateCcw, 
  Upload, 
  CheckCircle2,
  AlertCircle,
  Loader2,
  Square,
  Smartphone
} from "lucide-react";
import { API_URL } from "@/lib/api";

// ============================================
// Types
// ============================================

export interface VideoUploadValue {
  key: string;        // R2 object key
  filename: string;   // Generated filename
  size: number;       // File size in bytes
  mimetype: string;   // video/webm or video/mp4
  duration: number;   // Actual duration in seconds
}

interface VideoResponseRecorderProps {
  questionId: string;
  shareToken: string;
  maxDuration: number;
  allowRetake: boolean;
  value: VideoUploadValue | null;
  onChange: (value: VideoUploadValue | null) => void;
}

type RecordingState = 
  | "idle" 
  | "requesting" 
  | "preview" 
  | "recording" 
  | "recorded" 
  | "uploading" 
  | "uploaded" 
  | "error";

// ============================================
// Device Detection
// ============================================

function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function hasMediaRecorderSupport(): boolean {
  return typeof MediaRecorder !== "undefined";
}

// ============================================
// Main Component
// ============================================

export function VideoResponseRecorder({
  questionId,
  shareToken,
  maxDuration,
  allowRetake,
  value,
  onChange,
}: VideoResponseRecorderProps) {
  const isMobile = isMobileDevice();
  const [state, setState] = useState<RecordingState>(value ? "uploaded" : "idle");
  const [error, setError] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Refs for desktop recording
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordedBlobRef = useRef<Blob | null>(null);
  const recordedMimeTypeRef = useRef<string>("video/webm"); // Store MIME type explicitly

  // Cleanup on unmount
  const stopMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopMediaStream();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [stopMediaStream]);

  // ============================================
  // Desktop Recording Functions
  // ============================================

  // Get preferred MIME type for MediaRecorder
  const getPreferredMimeType = (): string => {
    const types = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    return types.find(type => MediaRecorder.isTypeSupported(type)) || "video/webm";
  };

  // Request camera/mic access (desktop)
  const startCamera = async () => {
    setState("requesting");
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: "user"
        },
        audio: true,
      });

      mediaStreamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // Prevent feedback
        await videoRef.current.play();
      }

      setState("preview");
    } catch (err) {
      console.error("Camera access error:", err);
      if (err instanceof DOMException) {
        if (err.name === "NotAllowedError") {
          setError("Camera/microphone access denied. Please allow access in your browser settings.");
        } else if (err.name === "NotFoundError") {
          setError("No camera or microphone found on this device.");
        } else {
          setError(`Camera error: ${err.message}`);
        }
      } else {
        setError("Failed to access camera. Please check permissions.");
      }
      setState("error");
    }
  };

  // Start recording (desktop)
  const startRecording = () => {
    if (!mediaStreamRef.current) return;

    chunksRef.current = [];
    const mimeType = getPreferredMimeType();
    
    // Store the MIME type for later use during upload
    recordedMimeTypeRef.current = mimeType;

    try {
      const recorder = new MediaRecorder(mediaStreamRef.current, {
        mimeType,
        videoBitsPerSecond: 2500000, // 2.5 Mbps
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        recordedBlobRef.current = blob;
        
        // Show recorded video for review
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = URL.createObjectURL(blob);
          videoRef.current.muted = false;
        }
        
        setState("recorded");
      };

      recorder.start(1000); // Collect data every second
      mediaRecorderRef.current = recorder;
      setRecordingTime(0);
      setState("recording");

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop at max duration
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } catch (err) {
      console.error("Recording error:", err);
      setError("Failed to start recording. Your browser may not support video recording.");
      setState("error");
    }
  };

  // Stop recording (desktop)
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  // Retake - reset and go back to preview (desktop)
  const handleRetakeDesktop = () => {
    recordedBlobRef.current = null;
    setRecordingTime(0);
    
    if (videoRef.current && mediaStreamRef.current) {
      videoRef.current.src = "";
      videoRef.current.srcObject = mediaStreamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play();
    }
    
    setState("preview");
  };

  // ============================================
  // Mobile File Capture Functions
  // ============================================

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Show video preview
      if (videoRef.current) {
        videoRef.current.src = URL.createObjectURL(file);
      }
      
      setState("recorded");
    }
  };

  const handleRetakeMobile = () => {
    setSelectedFile(null);
    if (videoRef.current) {
      videoRef.current.src = "";
    }
    setState("idle");
  };

  // ============================================
  // Upload Functions (shared)
  // ============================================

  const handleUpload = async () => {
    const fileToUpload = isMobile ? selectedFile : recordedBlobRef.current;
    if (!fileToUpload) return;

    setState("uploading");
    setUploadProgress(0);

    try {
      const formData = new FormData();
      
      // Generate filename and get MIME type
      let filename: string;
      let mimeType: string;
      
      if (isMobile && selectedFile) {
        filename = selectedFile.name;
        mimeType = selectedFile.type || "video/mp4";
      } else {
        // For recorded blob, use our stored MIME type (more reliable than blob.type)
        mimeType = recordedMimeTypeRef.current || "video/webm";
        const extension = mimeType.includes("mp4") ? "mp4" : "webm";
        filename = `video_${questionId}_${Date.now()}.${extension}`;
      }
      
      // Use base MIME type without codec parameters (codecs cause transmission issues)
      const baseMimeType = mimeType.split(";")[0].trim();
      
      // Read blob data and create fresh File with explicit type
      const arrayBuffer = await fileToUpload.arrayBuffer();
      const typedFile = new File([arrayBuffer], filename, { type: baseMimeType });
      
      formData.append("file", typedFile);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 5, 90));
      }, 200);

      const url = API_URL 
        ? `${API_URL}/api/public/surveys/${shareToken}/upload-video`
        : `/api/public/surveys/${shareToken}/upload-video`;

      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Upload failed");
      }

      // Get video duration if possible
      let duration = recordingTime;
      if (isMobile && videoRef.current) {
        duration = Math.round(videoRef.current.duration) || 0;
      }

      // Store the result
      const videoValue: VideoUploadValue = {
        key: data.file.key,
        filename: data.file.filename,
        size: data.file.size,
        mimetype: data.file.mimetype,
        duration,
      };

      onChange(videoValue);
      
      // Cleanup camera (desktop only)
      if (!isMobile) {
        stopMediaStream();
      }
      setState("uploaded");
    } catch (err) {
      console.error("Upload error:", err);
      setError(err instanceof Error ? err.message : "Upload failed");
      setState("recorded"); // Go back to recorded state to allow retry
    }
  };

  // Remove uploaded video (start over)
  const handleRemove = () => {
    onChange(null);
    recordedBlobRef.current = null;
    setSelectedFile(null);
    setRecordingTime(0);
    if (videoRef.current) {
      videoRef.current.src = "";
    }
    setState("idle");
  };

  // Cancel camera/recording
  const handleCancel = () => {
    stopMediaStream();
    stopRecording();
    recordedBlobRef.current = null;
    setRecordingTime(0);
    setState("idle");
  };

  // ============================================
  // Utility Functions
  // ============================================

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const _formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div className="space-y-4">
      {/* Camera Preview / Recorded Video */}
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
        {/* Video element - always rendered to ensure ref is available */}
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          autoPlay
          muted={state !== "recorded" && state !== "uploaded"}
          controls={state === "recorded" || state === "uploaded"}
        />
        
        {/* Idle State Overlay */}
        {state === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black">
            <Video className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-sm opacity-75">Click "Start Camera" to begin</p>
          </div>
        )}
        
        {/* Requesting State Overlay */}
        {state === "requesting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p className="text-sm">Requesting camera access...</p>
          </div>
        )}
        
        {/* Recording Indicator */}
        {state === "recording" && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-destructive/90 text-white px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-sm font-medium font-mono">
              {formatTime(recordingTime)} / {formatTime(maxDuration)}
            </span>
          </div>
        )}
        
        {/* Progress bar for recording */}
        {state === "recording" && (
          <div className="absolute bottom-0 left-0 right-0">
            <Progress 
              value={(recordingTime / maxDuration) * 100} 
              className="h-1 rounded-none" 
            />
          </div>
        )}
        
        {/* Mobile Fallback Notice */}
        {isMobile && state === "idle" && (
          <div className="absolute bottom-4 left-4 right-4">
            <Alert className="bg-background/95">
              <Smartphone className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Mobile detected. Your camera app will open for recording.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        {/* Idle: Start Camera */}
        {state === "idle" && (
          isMobile ? (
            <label className="cursor-pointer">
              <input
                type="file"
                accept="video/*"
                capture="user"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button asChild>
                <span>
                  <Video className="h-4 w-4 mr-2" />
                  Start Camera
                </span>
              </Button>
            </label>
          ) : (
            <Button onClick={startCamera} disabled={!hasMediaRecorderSupport()}>
              <Video className="h-4 w-4 mr-2" />
              Start Camera
            </Button>
          )
        )}
        
        {/* Preview: Start Recording */}
        {state === "preview" && (
          <Button onClick={startRecording} variant="destructive">
            <span className="w-3 h-3 bg-white rounded-full mr-2" />
            Start Recording
          </Button>
        )}
        
        {/* Recording: Stop */}
        {state === "recording" && (
          <Button onClick={stopRecording} variant="outline">
            <Square className="h-4 w-4 mr-2 fill-current" />
            Stop Recording
          </Button>
        )}
        
        {/* Recorded: Retake + Upload */}
        {state === "recorded" && (
          <>
            {allowRetake && (
              <Button variant="outline" onClick={isMobile ? handleRetakeMobile : handleRetakeDesktop}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Retake
              </Button>
            )}
            <Button onClick={handleUpload}>
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </>
        )}
        
        {/* Uploading: Progress */}
        {state === "uploading" && (
          <div className="w-full space-y-2">
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-sm text-center text-muted-foreground">
              Uploading... {uploadProgress}%
            </p>
          </div>
        )}
        
        {/* Uploaded: Success */}
        {state === "uploaded" && (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm font-medium">Video uploaded successfully</span>
            {allowRetake && (
              <Button variant="outline" size="sm" onClick={handleRemove} className="ml-2">
                <RotateCcw className="h-4 w-4 mr-1" />
                Record new
              </Button>
            )}
          </div>
        )}
        
        {/* Cancel button for preview/recording states (desktop) */}
        {!isMobile && (state === "preview" || state === "recording") && (
          <Button onClick={handleCancel} variant="ghost" size="sm">
            <VideoOff className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        )}
      </div>
      
      {/* Error State */}
      {state === "error" && error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {/* Duration Info */}
      <p className="text-xs text-muted-foreground text-center">
        Maximum recording time: {formatTime(maxDuration)}
      </p>
    </div>
  );
}

